import React, { useState, useEffect } from 'react';
import { ChevronDownIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { PlantInfo, plantApi } from '../../services/plantApi';

interface PlantSelectorProps {
  selectedPlant: string;
  onPlantChange: (plantSlug: string) => void;
}

const PlantSelector: React.FC<PlantSelectorProps> = ({ selectedPlant, onPlantChange }) => {
  const [plants, setPlants] = useState<PlantInfo[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlants();
  }, []);

  const fetchPlants = async () => {
    try {
      const response = await plantApi.getAvailablePlants();
      setPlants(response.plants);
    } catch (error) {
      console.error('Error fetching plants:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedPlantInfo = plants.find(p => p.slug === selectedPlant);

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg w-48"></div>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-w-[200px]"
      >
        <BuildingOfficeIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        <div className="flex-1 text-left">
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            {selectedPlantInfo?.name || 'Select Plant'}
          </div>
          {selectedPlantInfo && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {selectedPlantInfo.location} • {selectedPlantInfo.capacity_mw} MW
            </div>
          )}
        </div>
        <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
          {plants.map((plant) => (
            <button
              key={plant.id}
              onClick={() => {
                onPlantChange(plant.slug);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                selectedPlant === plant.slug ? 'bg-blue-50 dark:bg-blue-900/20' : ''
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {plant.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {plant.location} • {plant.capacity_mw} MW
                  </div>
                </div>
                <div className={`w-2 h-2 rounded-full ${
                  plant.is_active ? 'bg-green-500' : 'bg-gray-400'
                }`} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlantSelector;